<template>
  <FullCalendar :options="calendarOptions" />
</template>

<script>
import FullCalendar from "@fullcalendar/vue3";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import frLocale from "@fullcalendar/core/locales/fr";

export default {
  name: "CalendarView",
  components: { FullCalendar },
  props: {
    events: { type: Array, default: () => [] },
    locale: { type: String, default: "en" },
  },
  // rangeChange: the displayed period changed (also fired once at mount),
  // eventClick: a limitation was clicked (payload: the limitation).
  emits: ["rangeChange", "eventClick"],
  computed: {
    calendarOptions() {
      return {
        plugins: [dayGridPlugin, interactionPlugin],
        initialView: "dayGridMonth",
        locale: this.locale === "fr" ? frLocale : "en",
        firstDay: 1,
        height: "auto",
        editable: false,
        selectable: false,
        nowIndicator: true,
        eventTimeFormat: { hour: "2-digit", minute: "2-digit", hour12: false },
        // Month view only: the month on the left, navigation on the right
        headerToolbar: {
          left: "title",
          center: "",
          right: "today prev,next",
        },
        buttonIcons: { prev: "chevron-left", next: "chevron-right" },
        events: this.events,
        // Visibility: every limitation is a solid colored block (timed ones
        // are a dot + text by default in month view), days that overflow
        // get a "+N" popover instead of growing.
        eventDisplay: "block",
        displayEventEnd: true,
        dayMaxEvents: true,
        eventContent: this.renderEvent,
        datesSet: (info) =>
          this.$emit("rangeChange", { start: info.start, end: info.end }),
        eventClick: (info) =>
          this.$emit("eventClick", info.event.extendedProps.limitation),
      };
    },
  },
  methods: {
    renderEvent(arg) {
      // Two lines: producer, then origin · power (· time range)
      const lim = arg.event.extendedProps.limitation;
      if (!lim) return { html: this.escape(arg.event.title) };
      const producer = lim.producer ? lim.producer.display_name : "";
      const origin = lim.origin ? lim.origin.label : "";
      const details = [origin, `${lim.power_limit} kW`];
      if (arg.timeText) details.push(arg.timeText);
      return {
        html:
          `<div class="lim-event__producer">${this.escape(producer)}</div>` +
          `<div class="lim-event__details">${this.escape(details.join(" · "))}</div>`,
      };
    },
    escape(text) {
      return String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
  },
};
</script>

<style>
/* Toolbar: same look as the app buttons */
.fc .fc-toolbar.fc-header-toolbar {
  margin: 0 12px 12px;
}
.fc .fc-toolbar-title {
  font-size: 1.5rem;
  font-weight: 600;
  text-transform: capitalize;
}
.fc .fc-button-primary {
  background-color: #fff;
  border-color: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-primary));
  border-radius: 4px;
  padding: 8px 18px;
  font-size: 0.95rem;
  font-weight: 500;
  text-transform: capitalize;
  box-shadow: none;
}
.fc .fc-button .fc-icon {
  font-size: 1.3rem;
}
.fc .fc-button-primary:hover,
.fc .fc-button-primary:not(:disabled):active,
.fc .fc-button-primary:not(:disabled).fc-button-active {
  background-color: rgb(var(--v-theme-primary));
  border-color: rgb(var(--v-theme-primary));
  color: #fff;
}
.fc .fc-button-primary:disabled {
  background-color: #fff;
  border-color: rgba(0, 0, 0, 0.26);
  color: rgba(0, 0, 0, 0.38);
}
.fc .fc-button-primary:focus {
  box-shadow: none;
}
.fc .fc-today-button {
  margin-right: 12px;
}

/* Limitation blocks in the calendar */
.fc .fc-daygrid-event {
  padding: 2px 4px;
  border-width: 0 0 0 4px;
  border-radius: 3px;
  color: #fff;
  white-space: normal;
  line-height: 1.2;
  cursor: pointer;
}
.fc .fc-daygrid-event:hover {
  filter: brightness(1.1);
}
.fc .lim-event__producer {
  font-weight: 600;
  font-size: 0.8rem;
}
.fc .lim-event__details {
  font-size: 0.72rem;
  opacity: 0.9;
}
/* Open-ended limitation (no end): striped block */
.fc .limitation-open {
  background-image: repeating-linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.18) 0 6px,
    transparent 6px 12px
  );
  opacity: 1;
  border-style: dashed;
}
/* Lighter weekends, clearer "today" */
.fc .fc-day-sat,
.fc .fc-day-sun {
  background-color: rgba(0, 0, 0, 0.025);
}
.fc .fc-day-today {
  background-color: rgba(0, 147, 191, 0.08) !important;
}
.fc .fc-more-link {
  font-weight: 600;
}
</style>
